import { useFrame, useLoader } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import {
  AmbientLight,
  Color,
  DirectionalLight,
  Mesh,
  MeshStandardMaterial,
  NoColorSpace,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  TextureLoader,
  Vector2,
  Vector4,
} from "three";
import type { WebGLRenderer } from "three";
import { MOON_RADIUS } from "../simulation/constants";
import type { SimulationSnapshot } from "../simulation/types";
import { getObserverFrame } from "./observerFrame";
import { earthshineIntensity } from "./sceneLighting";

const INSET_CAMERA_DISTANCE = 3;
const INSET_CAMERA_FIELD_OF_VIEW_DEGREES = 36;
const INSET_LIGHT_DISTANCE = 5;
const INSET_BACKDROP_COLOR = new Color("#050a14");

interface RendererState {
  readonly autoClear: boolean;
  readonly autoClearColor: boolean;
  readonly autoClearDepth: boolean;
  readonly autoClearStencil: boolean;
  readonly scissor: Vector4;
  readonly scissorTest: boolean;
  readonly viewport: Vector4;
}

function saveRendererState(
  renderer: WebGLRenderer,
  scissor: Vector4,
  viewport: Vector4,
): RendererState {
  return {
    autoClear: renderer.autoClear,
    autoClearColor: renderer.autoClearColor,
    autoClearDepth: renderer.autoClearDepth,
    autoClearStencil: renderer.autoClearStencil,
    scissor: renderer.getScissor(scissor),
    scissorTest: renderer.getScissorTest(),
    viewport: renderer.getViewport(viewport),
  };
}

function restoreRendererState(renderer: WebGLRenderer, state: RendererState) {
  renderer.autoClear = state.autoClear;
  renderer.autoClearColor = state.autoClearColor;
  renderer.autoClearDepth = state.autoClearDepth;
  renderer.autoClearStencil = state.autoClearStencil;
  renderer.setViewport(state.viewport);
  renderer.setScissor(state.scissor);
  renderer.setScissorTest(state.scissorTest);
}

export function EarthViewPass({
  snapshot,
  targetSelector,
}: {
  readonly snapshot: SimulationSnapshot;
  readonly targetSelector: string;
}) {
  const sourceTexture = useLoader(TextureLoader, "/textures/moon-lroc.jpg");
  const inset = useMemo(() => {
    const texture = sourceTexture.clone();
    texture.anisotropy = 8;
    texture.colorSpace = SRGBColorSpace;
    texture.needsUpdate = true;
    const bumpMap = sourceTexture.clone();
    bumpMap.anisotropy = 8;
    bumpMap.colorSpace = NoColorSpace;
    bumpMap.needsUpdate = true;

    const geometry = new SphereGeometry(MOON_RADIUS, 64, 32);
    const material = new MeshStandardMaterial({
      bumpMap,
      bumpScale: 0.018,
      map: texture,
      roughness: 0.94,
    });
    const moon = new Mesh(geometry, material);
    const light = new DirectionalLight(new Color("#fff8ef"), 3.2);
    // Cool earthshine from the observer (Earth sits at +Z in this frame).
    const earthshine = new DirectionalLight(new Color("#8fb6ff"), 0);
    earthshine.position.set(0, 0, INSET_LIGHT_DISTANCE);
    // Faint fill so the unlit part reads as a dark disk, not a hole.
    const fill = new AmbientLight(new Color("#2a3548"), 0.12);
    const scene = new Scene();
    const camera = new PerspectiveCamera(
      INSET_CAMERA_FIELD_OF_VIEW_DEGREES,
      1,
      0.1,
      10,
    );

    camera.position.set(0, 0, INSET_CAMERA_DISTANCE);
    camera.up.set(0, 1, 0);
    camera.lookAt(0, 0, 0);
    // Synchronous rotation maps lunar local +Y to observer up and local +Z
    // toward Earth, so the observer-local Moon is north-up at identity.
    scene.add(moon, light, light.target, earthshine, earthshine.target, fill);

    return {
      bumpMap,
      camera,
      earthshine,
      geometry,
      light,
      material,
      scene,
      texture,
    };
  }, [sourceTexture]);
  const frame = useMemo(() => getObserverFrame(snapshot), [snapshot]);
  const rendererScratch = useMemo(
    () => ({
      clearColor: new Color(),
      rendererSize: new Vector2(),
      scissor: new Vector4(),
      viewport: new Vector4(),
    }),
    [],
  );

  useEffect(
    () => () => {
      inset.bumpMap.dispose();
      inset.geometry.dispose();
      inset.material.dispose();
      inset.texture.dispose();
    },
    [inset],
  );

  useFrame(({ camera, gl, scene }) => {
    const rendererState = saveRendererState(
      gl,
      rendererScratch.scissor,
      rendererScratch.viewport,
    );

    try {
      const rendererSize = gl.getSize(rendererScratch.rendererSize);
      gl.setScissorTest(false);
      gl.setViewport(0, 0, rendererSize.x, rendererSize.y);
      gl.autoClear = true;
      gl.autoClearColor = true;
      gl.autoClearDepth = true;
      gl.autoClearStencil = true;
      gl.render(scene, camera);

      const target = document.querySelector<HTMLElement>(targetSelector);
      if (target === null) {
        return;
      }

      const canvasBounds = gl.domElement.getBoundingClientRect();
      const targetBounds = target.getBoundingClientRect();
      if (
        canvasBounds.width <= 0 ||
        canvasBounds.height <= 0 ||
        targetBounds.width <= 0 ||
        targetBounds.height <= 0
      ) {
        return;
      }

      const clippedLeft = Math.max(targetBounds.left, canvasBounds.left);
      const clippedRight = Math.min(targetBounds.right, canvasBounds.right);
      const clippedTop = Math.max(targetBounds.top, canvasBounds.top);
      const clippedBottom = Math.min(targetBounds.bottom, canvasBounds.bottom);
      if (clippedRight <= clippedLeft || clippedBottom <= clippedTop) {
        return;
      }

      const scaleX = rendererSize.x / canvasBounds.width;
      const scaleY = rendererSize.y / canvasBounds.height;
      const left = (clippedLeft - canvasBounds.left) * scaleX;
      const bottom = (canvasBounds.bottom - clippedBottom) * scaleY;
      const width = (clippedRight - clippedLeft) * scaleX;
      const height = (clippedBottom - clippedTop) * scaleY;

      inset.earthshine.intensity = earthshineIntensity(
        snapshot.illuminatedFraction,
      );
      inset.earthshine.target.updateMatrixWorld();
      inset.camera.aspect = width / height;
      inset.camera.updateProjectionMatrix();
      inset.light.position
        .set(...frame.lightLocal)
        .multiplyScalar(INSET_LIGHT_DISTANCE);
      inset.light.target.updateMatrixWorld();

      gl.autoClear = false;
      gl.setViewport(left, bottom, width, height);
      gl.setScissor(left, bottom, width, height);
      gl.setScissorTest(true);
      const previousClearColor = gl.getClearColor(rendererScratch.clearColor);
      const previousClearAlpha = gl.getClearAlpha();
      gl.setClearColor(INSET_BACKDROP_COLOR, 1);
      gl.clear(true, true, false);
      gl.render(inset.scene, inset.camera);
      gl.setClearColor(previousClearColor, previousClearAlpha);
    } finally {
      restoreRendererState(gl, rendererState);
    }
  }, 1);

  return null;
}
